import type { Command } from "../document/commands.ts";
import type { WorldDocument } from "../document/types.ts";
import { anchoredIn, assertAreaRect, type AreaRect } from "./area.ts";
import { areaHolds, areaMapOf } from "./area-move.ts";
import { doorSides, gridCommand, isGridLayer, planGrid } from "./area-grids.ts";
import { waysTo, type Ways } from "./door-links.ts";
import { mapReferences, referrersOf, type ReferenceLink, type ReferenceThing, type ReferenceTypes } from "./references.ts";

/**
 * Delete over an area (ADR-128 decision 33): the move's planner with no destination. Every taken grid cell in the
 * rectangle is cleared, a terrain layer's whole corner rectangle with it, so the cells cleared are the cells the
 * area counts and paint that runs on past the rectangle ends at its edge; and every prop and marker anchored inside
 * on a taken layer is removed, as one compound and one undo. Each layer's removals run from its last index down, so
 * none shifts the index of another and the inverse adds them back from the first up. A prop or marker that
 * something outside the area points at stops the delete and is named, by the checks that guard a single delete: a
 * typed reference from a thing the delete keeps, and a door or a room's host that leads to an arrival point it
 * removes.
 *
 * A door of a taken door layer whose every painted cell lies inside the rectangle leaves the layer's list in the
 * same compound, after the stroke that clears its cells and from the list's last index down, so one undo puts the
 * door back and repaints it; such a door no longer leads anywhere and does not stop the delete of its arrival
 * point. A door with a cell outside the rectangle, or on a layer out of reach, keeps its definition and stops the
 * delete of its arrival point as any door does. A door that paints no cell is not in the rectangle and stays.
 */

/**
 * What the reference check reads: the registered types, whose schemas say which fields are references, and the
 * entity type the game treats as an arrival point, absent when it names none.
 */
export interface AreaDeleteGuard {
  readonly types: ReferenceTypes;
  readonly arrivalType?: string;
}

/**
 * A planned delete: nothing to do; refused, with the links it would break and what leads to the arrival points it
 * would remove; or the command with the layers it writes in the map's order, the cells it clears and the props,
 * markers and doors it removes.
 */
export type AreaDeletePlan =
  | { readonly kind: "nothing" }
  | { readonly kind: "refused"; readonly referrers: readonly ReferenceLink[]; readonly ways: Ways }
  | { readonly kind: "delete"; readonly command: Extract<Command, { readonly kind: "compound" }>; readonly layers: readonly string[]; readonly changes: number; readonly props: number; readonly markers: number; readonly doors: number };

/**
 * A door of the map in view as one key, for the set of doors a delete removes.
 *
 * @param layerId The door's layer.
 * @param doorId The door.
 * @returns The key.
 */
function wayKey(layerId: string, doorId: string): string {
  return JSON.stringify([layerId, doorId]);
}

/**
 * Plans the delete of an area.
 *
 * @param world The world as it stands when Delete is pressed.
 * @param mapId The map in view.
 * @param rect The area's rectangle of tiles.
 * @param layerIds The layers the reach takes (`areaReach`); only these are read or written.
 * @param guard What the reference check reads.
 * @returns The plan.
 * @throws Error when the map is missing or a zone or door byte names nothing; RangeError when the rectangle is not
 *   one of whole tiles inside the map.
 */
export function planAreaDelete(world: WorldDocument, mapId: string, rect: AreaRect, layerIds: readonly string[], guard: AreaDeleteGuard): AreaDeletePlan {
  const map = areaMapOf(world, mapId);
  assertAreaRect(map, rect);
  const taken = new Set(layerIds);
  if (!areaHolds(map, taken, rect)) {
    return { kind: "nothing" };
  }
  const things: ReferenceThing[] = [];
  const going = new Set<string>();
  const doors: Ways["doors"][number][] = [];
  const hosts: Ways["hosts"][number][] = [];
  const grids: Command[] = [];
  const items: Command[] = [];
  const written: string[] = [];
  let changes = 0;
  let props = 0;
  let markers = 0;
  let removed = 0;
  for (const layer of map.layers) {
    if (!taken.has(layer.id)) {
      continue;
    }
    const before = grids.length + items.length;
    if (layer.kind === "door") {
      const sides = doorSides(map, layer, rect);
      for (let index = layer.doors.length - 1; index >= 0; index -= 1) {
        const door = layer.doors[index];
        if (door !== undefined && sides[index]?.inside === true && sides[index]?.outside === false) {
          going.add(wayKey(layer.id, door.id));
          items.push({ kind: "removeDoor", mapId, layerId: layer.id, index, door });
          removed += 1;
        }
      }
    }
    if (layer.kind === "prop") {
      const removals: Command[] = [];
      layer.props.forEach((prop, index) => {
        if (anchoredIn(rect, prop.x, prop.y)) {
          things.push({ kind: "prop", layerId: layer.id, propId: prop.id });
          removals.push({ kind: "removeProp", mapId, layerId: layer.id, index, prop });
        }
      });
      props += removals.length;
      items.push(...removals.reverse());
    } else if (layer.kind === "entity") {
      const removals: Command[] = [];
      layer.entities.forEach((entity, index) => {
        if (anchoredIn(rect, entity.x, entity.y)) {
          things.push({ kind: "marker", layerId: layer.id, entityId: entity.id });
          removals.push({ kind: "removeEntity", mapId, layerId: layer.id, index, entity });
          if (guard.arrivalType !== undefined && entity.type === guard.arrivalType) {
            const ways = waysTo(world, mapId, entity.id);
            doors.push(...ways.doors);
            hosts.push(...ways.hosts);
          }
        }
      });
      markers += removals.length;
      items.push(...removals.reverse());
    } else if (isGridLayer(layer)) {
      const plan = planGrid(map, layer, rect, null);
      if (plan.changes.length > 0) {
        grids.push(gridCommand(mapId, layer, plan.changes, world.tilesets));
        changes += plan.changes.length;
      }
    }
    if (grids.length + items.length > before) {
      written.push(layer.id);
    }
  }
  const referrers = referrersOf(mapReferences(map, guard.types), things.map((thing) => (thing.kind === "prop" ? thing.propId : thing.entityId)));
  const leading = doors.filter((way) => way.mapId !== mapId || !going.has(wayKey(way.layerId, way.doorId)));
  if (referrers.length > 0 || leading.length > 0 || hosts.length > 0) {
    return { kind: "refused", referrers, ways: { doors: leading, hosts } };
  }
  if (grids.length + items.length === 0) {
    return { kind: "nothing" };
  }
  return { kind: "delete", command: { kind: "compound", commands: [...grids, ...items] }, layers: written, changes, props, markers, doors: removed };
}
